import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import type { Client } from "@twilio/conversations";
import { Box } from "@twilio-paste/core";

import NewConversationDetails from "../NewConversationDetails";
import ActionErrorModal from "../../modals/ActionErrorModal";
import ConversationEmptyState from "./ConversationEmptyState";
import ConversationLoadingState from "./ConversationLoadingState";
import NewConversationContent from "./NewConversationContent";

import { ConversationDto } from "../../../dto/conversation";
import { actionCreators, AppState } from "../../../store";
import { getTranslation } from "../../../utils/localUtils";
import { useTwilioConversation } from "../../../hooks/twilio/useTwilioConversation";
import styles from "../../../styles";
import { ERROR_MODAL_MESSAGES } from "../../../constants";
import { isTemplateOnly, isInputDisabled } from "../../../utils/conversation";

interface NewConversationContainerProps {
  conversation: ConversationDto;
  client?: Client;
}

const NewConversationContainer: React.FC<NewConversationContainerProps> = (
  props
) => {
  const dispatch = useDispatch();

  const { pushMessages, updateParticipants, removeConversation } = useMemo(
    () => bindActionCreators(actionCreators, dispatch),
    [dispatch]
  );

  const local = useSelector((s: AppState) => s.local);
  const greeting = getTranslation(local, "greeting");

  const loadingStatus = useSelector((s: AppState) => s.loadingStatus);
  const participantsBySid = useSelector((s: AppState) => s.participants);
  const messagesBySid = useSelector((s: AppState) => s.messages);
  const typingBySid = useSelector((s: AppState) => s.typingData);
  const lastReadIndex = useSelector((s: AppState) => s.lastReadIndex);
  const use24hTimeFormat = useSelector((s: AppState) => s.use24hTimeFormat);

  const convoDto = props.conversation;
  const selectedSid = convoDto?.twilioSid;

  const participants = selectedSid ? participantsBySid[selectedSid] ?? [] : [];

  const messages = selectedSid ? messagesBySid[selectedSid] ?? [] : [];

  const typingData = selectedSid ? typingBySid[selectedSid] ?? [] : [];

  const templateOnly = convoDto ? isTemplateOnly(convoDto) : false;
  const inputDisabled = convoDto ? isInputDisabled(convoDto) : false;

  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);

  const handleForbidden = useCallback(
    (twilioSid: string) => {
      removeConversation(twilioSid);
    },
    [removeConversation]
  );

  const {
    sdkConvo,
    isLoading: isSdkConvoLoading,
    hasLoadError,
    errorData,
    isErrorModalOpen,
    reload: reloadConversation,
    closeErrorModal,
  } = useTwilioConversation(props.client, convoDto, handleForbidden);

  const sdkConvoSid = sdkConvo?.sid;

  const isSameConversation =
    !!selectedSid && !!sdkConvoSid && selectedSid === sdkConvoSid;

  const isInitialLoad = !!selectedSid && (!sdkConvo || !isSameConversation);

  const showContent =
    !!props.client &&
    !!convoDto &&
    !!selectedSid &&
    !!sdkConvo &&
    isSameConversation;

  useEffect(() => {
    if (!sdkConvo) return;
    if (!isSameConversation) return;
    if (participants.length > 0) return;

    let cancelled = false;

    sdkConvo
      .getParticipants()
      .then((result) => {
        if (!cancelled) {
          updateParticipants(result, sdkConvo.sid);
        }
      })
      .catch((e) => console.error("[Twilio] getParticipants error:", e));

    return () => {
      cancelled = true;
    };
  }, [sdkConvo, isSameConversation, participants.length, updateParticipants]);

  if (!selectedSid || !convoDto) {
    return <ConversationEmptyState greeting={greeting} />;
  }

  return (
    <Box style={styles.convosWrapperBox}>
      <ActionErrorModal
        errorText={
          isErrorModalOpen
            ? ERROR_MODAL_MESSAGES.SHOW_CONVERSATION
            : ERROR_MODAL_MESSAGES.CHANGE_CONVERSATION_NAME
        }
        isOpened={isErrorModalOpen}
        onClose={closeErrorModal}
        error={errorData}
      />

      {isSameConversation && sdkConvo ? (
        <NewConversationDetails
          convoSid={selectedSid}
          convo={convoDto}
          participants={participants}
          sdkConvo={sdkConvo}
        />
      ) : null}

      {!showContent ? (
        <ConversationLoadingState
          isLoading={isInitialLoad || isSdkConvoLoading}
          hasLoadError={hasLoadError}
          onReload={reloadConversation}
        />
      ) : (
        <NewConversationContent
          sid={selectedSid}
          id={convoDto.id}
          client={props.client!}
          sdkConvo={sdkConvo}
          messages={messages}
          participants={participants}
          typingData={typingData}
          lastReadIndex={lastReadIndex}
          loadingStatus={loadingStatus}
          use24hTimeFormat={use24hTimeFormat}
          droppedFiles={droppedFiles}
          onDroppedFilesChange={setDroppedFiles}
          pushMessages={pushMessages}
          templateOnly={templateOnly}
          inputDisabled={inputDisabled}
        />
      )}
    </Box>
  );
};

export default NewConversationContainer;
